// 앱이 띄운 창의 더미(슬랙 모달의 view stack). 세 겹까지 쌓이고 맨 위만 보인다.
//
// 서버가 더미의 진실을 들고 있고(0043 `under`), 여기서는 **받은 창을 어디에 둘지**만 정한다.
// 열기·얹기·고치기 셋 다 `view_opened` 한 사건으로 오므로, 창이 가리키는 `under` 로 가른다:
// - 이미 있는 번호면 그 자리에서 바뀐 것이다(앱의 `views.update`).
// - `under` 가 없으면 새 뿌리다 — 앞의 더미는 서버가 이미 지웠다.
// - `under` 가 더미에 있으면 그 위에 얹힌 것이다. 그 위에 있던 것은 버린다.
// 아래 창은 그리던 그대로 남겨 둔다(숨길 뿐) — 위 창을 닫으면 적던 글이 그대로 보여야 한다.

import type { View } from '@maru/protocol'

/** 더미의 한 겹. `turn` 은 앱이 창을 바꿀 때마다 올라 칸을 새로 채우게 한다. */
export interface Modal {
  id: number
  view: View
  errors: Record<string, string>
  busy: boolean
  failure?: string | undefined
  turn: number
}

/** 아래 → 위. 비었으면 창이 없다. */
export type Stack = readonly Modal[]

export function fresh(id: number, view: View): Modal {
  return { id, view, errors: {}, busy: false, turn: 0 }
}

/** 서버에서 받은 창을 더미에 둔다. */
export function placed(stack: Stack, got: { id: number; view: View; under: number | null }): Stack {
  const at = stack.findIndex((m) => m.id === got.id)
  if (at >= 0) {
    const was = stack[at] as Modal
    return stack.map((m, i) => (i === at ? { ...fresh(got.id, got.view), turn: was.turn + 1 } : m))
  }
  if (got.under === null) return [fresh(got.id, got.view)]
  const below = stack.findIndex((m) => m.id === got.under)
  // 모르는 창 위에 얹혔다 — 사건을 놓친 것이다. 받은 것만이라도 보여 준다.
  if (below < 0) return [fresh(got.id, got.view)]
  return [...stack.slice(0, below + 1), fresh(got.id, got.view)]
}

/** 이 창과 그 위를 걷어 낸다. 서버의 `ON DELETE CASCADE` 와 같다. */
export function popped(stack: Stack, id: number): Stack {
  const at = stack.findIndex((m) => m.id === id)
  return at < 0 ? stack : stack.slice(0, at)
}

/** 한 겹을 고친다. 없으면 그대로. */
export function patched(stack: Stack, id: number, f: (m: Modal) => Modal): Stack {
  return stack.map((m) => (m.id === id ? f(m) : m))
}
